import { useEffect, useId, useRef } from 'react'

let nanoGalleryLoaded = false
let jQueryLoader: Promise<typeof import('jquery')> | null = null
const NANOGALLERY_SCRIPT_ID = 'nanogallery2-script'
const NANOGALLERY_CDN = 'https://cdn.jsdelivr.net/npm/nanogallery2@3.0.5/dist/jquery.nanogallery2.min.js'
const NANOGALLERY_STYLES_ID = 'nanogallery2-styles'
const NANOGALLERY_CSS = 'https://cdn.jsdelivr.net/npm/nanogallery2@3.0.5/dist/css/nanogallery2.min.css'

interface NanoGalleryItem {
    src: string
    thumb?: string
    title?: string
}

interface NanoGalleryViewerProps {
    items: NanoGalleryItem[]
    initialIndex?: number
    className?: string
    autoplay?: boolean
}

declare global {
    interface Window {
        jQuery?: typeof import('jquery')
        $?: typeof import('jquery')
    }
    interface JQuery {
        nanogallery2: (...args: any[]) => any
    }
}

const getJQuery = async () => {
    if (jQueryLoader) return jQueryLoader
    jQueryLoader = import('jquery').then((mod) => {
        const jq = mod.default || (mod as unknown as typeof import('jquery'))
        if (typeof window !== 'undefined') {
            window.$ = window.$ || (jq as any)
            window.jQuery = window.jQuery || (jq as any)
        }
        return jq
    })
    return jQueryLoader
}

function loadScript(src: string, id: string) {
    return new Promise<void>((resolve, reject) => {
        if (typeof document === 'undefined') return resolve()
        const existing = document.getElementById(id) as HTMLScriptElement | null
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve()
                return
            }
            existing.addEventListener('load', () => resolve(), { once: true })
            existing.addEventListener('error', (event) => reject(event), { once: true })
            return
        }
        const script = document.createElement('script')
        script.id = id
        script.src = src
        script.async = true
        script.dataset.loaded = 'false'
        script.addEventListener('load', () => {
            script.dataset.loaded = 'true'
            resolve()
        })
        script.addEventListener('error', (event) => reject(event))
        document.body.appendChild(script)
    })
}

function loadStylesheet(href: string, id: string) {
    return new Promise<void>((resolve, reject) => {
        if (typeof document === 'undefined') return resolve()
        const existing = document.getElementById(id) as HTMLLinkElement | null
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve()
                return
            }
            existing.addEventListener('load', () => resolve(), { once: true })
            existing.addEventListener('error', (event) => reject(event), { once: true })
            return
        }
        const link = document.createElement('link')
        link.id = id
        link.rel = 'stylesheet'
        link.href = href
        link.dataset.loaded = 'false'
        link.addEventListener('load', () => {
            link.dataset.loaded = 'true'
            resolve()
        })
        link.addEventListener('error', (event) => reject(event))
        document.head.appendChild(link)
    })
}

async function ensureNanoGalleryLoaded() {
    if (nanoGalleryLoaded) return
    if (typeof window === 'undefined') return
    await getJQuery()
    await Promise.all([
        loadStylesheet(NANOGALLERY_CSS, NANOGALLERY_STYLES_ID),
        loadScript(NANOGALLERY_CDN, NANOGALLERY_SCRIPT_ID),
    ])
    nanoGalleryLoaded = true
}

export function NanoGalleryViewer({ items, initialIndex = 0, className, autoplay = false }: NanoGalleryViewerProps) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const galleryId = useId().replace(/:/g, '')
    const normalizedItems = items.filter((item) => Boolean(item?.src))
    const itemsMemoKey = normalizedItems
        .map((item) => `${item.src}|${item.title ?? ''}|${item.thumb ?? ''}`)
        .join(';')

    useEffect(() => {
        if (!containerRef.current || !normalizedItems.length) return
        let disposed = false
        let initialized = false
        const hostElement = containerRef.current

        void ensureNanoGalleryLoaded().then(async () => {
            if (disposed || !hostElement) return
            const jq = window.jQuery || (await getJQuery())
            const host = jq(hostElement)

            if (!host.nanogallery2) return

            host.empty()
            const inner = document.createElement('div')
            inner.className = 'nanogallery2-instance'
            hostElement.appendChild(inner)
            const galleryElement = jq(inner)

            galleryElement.nanogallery2({
                items: normalizedItems.map((item, idx) => ({
                    src: item.src,
                    srct: item.thumb || item.src,
                    title: item.title || `Artwork ${idx + 1}`,
                })),
                galleryDisplayMode: 'fullContent',
                galleryTheme: {
                    navigationPaginationHeight: 0,
                    thumbnail: { borderRadius: '20px' },
                },
                galleryLastRowFull: true,
                viewerFullscreen: false,
                viewerZoom: true,
                slideshowAutoStart: autoplay && normalizedItems.length > 1,
                viewerToolbar: {
                    standard: 'minimizeButton,downloadButton',
                    mediaFullWidth: 'zoomButton,fullscreenButton',
                },
                thumbnailHeight: 320,
                thumbnailWidth: 'auto',
                thumbnailsColumns: 1,
                thumbnailAlignment: 'center',
                thumbnailOpenImage: true,
                locationHash: false,
            })
            galleryElement.one('galleryDisplayed.nanogallery2', () => {
                if (disposed) return
                try {
                    const data = galleryElement.nanogallery2('data') as { items?: Array<{ kind: string; albumID?: string | number; GetID?: () => string | number; ID?: string | number }> }
                    const imageItems = data?.items?.filter((item) => item.kind === 'image') ?? []
                    const safeIndex = Math.min(Math.max(initialIndex, 0), imageItems.length - 1)
                    const imageItem = imageItems[safeIndex] || imageItems[0]
                    if (imageItem) {
                        const albumID = imageItem.albumID != null ? String(imageItem.albumID) : '-1'
                        const imageID =
                            typeof imageItem.GetID === 'function'
                                ? String(imageItem.GetID())
                                : imageItem.ID != null
                                  ? String(imageItem.ID)
                                  : '0'
                        galleryElement.nanogallery2('displayItem', `${albumID}/${imageID}`)
                    }
                } catch {
                    // ignore display errors
                }
            })
            initialized = true
        })

        return () => {
            disposed = true
            if (!initialized) return
            try {
                const jq = window.jQuery || $
                const host = jq(containerRef.current)
                const galleryElement = host.find('.nanogallery2-instance')
                if (galleryElement?.nanogallery2) {
                    galleryElement.nanogallery2('destroy')
                }
                host.empty()
            } catch {
                // ignore cleanup errors
            }
        }
    }, [galleryId, itemsMemoKey, initialIndex, autoplay])

    return <div id={`gallery-${galleryId}`} ref={containerRef} className={`ngy2-wrapper ${className || ''}`.trim()} />
}
